import type { Api, Schema } from '@cdn/api';
import { Roles } from '@cdn/contracts';
import type { Session } from '@cdn/identity';
import { Observable, Resource } from '@cdn/state';

interface Specs {
	api: Api;
	session: Session;
}

/**
 * The signed-in person's own internal admission to CDN management and preparation, as `waitlist.admission.read`
 * answers it. The service decides everything: who is admitted, on which basis (an operator's decision, its
 * operator allowlist or the open policy) and what a request changes. This model only reads the answer, asks
 * for admission, and tells the interface which capabilities to leave out meanwhile.
 *
 * It reads the admission whenever another account becomes active, forgets it when the account leaves, and
 * reads it again after any operation was refused with `ADMISSION_REQUIRED`, which covers a revocation while
 * the page is open. Until the first answer arrives the gated capabilities wait, so a person who is not
 * admitted is never offered a change that disappears a moment later. When the read fails nothing is left out:
 * the service still refuses what the person may not do, and that refusal brings the admission here.
 */
export class Admission extends Observable {
	#api: Api;
	#session: Session;
	#user: string | undefined;
	#releases: Array<() => void> = [];

	readonly document = new Resource<Schema.Admission>();
	/** The admission of the selected organization (decision E3): once it is admitted its members work by role */
	readonly organization = new Resource<Schema.OrganizationAdmission>();
	#organization: string | undefined;

	/** Whether the service answered that neither the person nor the selected organization is admitted */
	get awaiting(): boolean {
		if (this.organization.value?.admitted) return false;
		return this.document.value?.admitted === false;
	}

	/** Whether the gated capabilities are decided: an answer arrived, or the read failed and the service decides */
	get settled(): boolean {
		return this.document.value !== undefined || this.document.status === 'failed';
	}

	/** Whether the person's role can ask for the organization's admission (owners and administrators) */
	get asks(): boolean {
		return this.#session.can('plan.request');
	}

	get state(): Schema.Admission['state'] {
		return this.document.value?.state ?? 'none';
	}

	constructor({ api, session }: Specs) {
		super();
		this.#api = api;
		this.#session = session;
		this.#releases.push(
			this.document.subscribe(() => this.changed()),
			this.organization.subscribe(() => this.changed()),
			session.subscribe(() => this.#follow()),
			api.refused(() => void (this.#user && this.load()))
		);
		this.#follow();
		// A decision taken while the person waited is read when they come back to the page, since no refusal
		// arrives while the gated actions stay hidden
		if (typeof document === 'undefined') return;
		const visible = () => document.visibilityState === 'visible' && this.#user && this.awaiting && void this.load();
		document.addEventListener('visibilitychange', visible);
		this.#releases.push(() => document.removeEventListener('visibilitychange', visible));
	}

	/**
	 * Whether a capability of the selected organization's membership is available now: the role must hold
	 * it, and a capability the contract gates on admission also needs the person to be admitted.
	 */
	can(capability: string): boolean {
		if (!this.#session.can(capability)) return false;
		if (!Roles.admits(capability)) return true;
		// Before the first answer the capability waits; after a failed read the service decides alone
		return this.settled && !this.awaiting;
	}

	/**
	 * Whether the role holds the capability and only the missing admission keeps it out, so the interface
	 * explains admission instead of the role
	 */
	withholds(capability: string): boolean {
		return this.settled && this.#session.can(capability) && !this.can(capability);
	}

	load() {
		this.#standing();
		return this.document.load(() => this.#api.call('waitlist.admission.read'));
	}

	// The selected organization's admission; a failed read leaves the person's own to decide
	#standing() {
		const organization = this.#session.organization?.organization;
		if (organization) void this.organization.load(() => this.#api.call('waitlist.organization.read', { params: { organization } })).catch(() => undefined);
	}

	/**
	 * Asks for the admission of the selected organization (an owner or an administrator), or updates its note
	 *
	 * @throws {ApiProblem}
	 */
	async seek(note?: string): Promise<Schema.OrganizationAdmission | undefined> {
		const organization = this.#session.organization?.organization;
		if (!organization) return undefined;
		const answer = await this.#api.call('waitlist.organization.request', { params: { organization }, body: note ? { note } : {} });
		this.organization.set(answer);
		return answer;
	}

	/**
	 * Asks for admission, or updates the note of the request. Asking admits nobody and never undoes a decision
	 * of the Beyond team, so the answer keeps whatever state the service holds.
	 *
	 * @throws {ApiProblem}
	 */
	async request(note?: string): Promise<Schema.Admission> {
		const answer = await this.#api.call('waitlist.admission.request', { body: note ? { note } : {} });
		this.document.set(answer);
		return answer;
	}

	destroy() {
		for (const release of this.#releases) release();
	}

	#follow() {
		const session = this.#session;
		if (session.status === 'anonymous') return this.#forget();
		if (session.status !== 'active') return;

		const user = session.user?.id;
		const organization = session.organization?.organization;
		if (user === this.#user && organization === this.#organization) return;
		const same = user === this.#user;
		if (!same) this.#forget();
		this.#user = user;
		this.#organization = organization;
		this.organization.clear();
		// Another organization of the same person: only its admission is read again
		if (same) return void this.#standing();
		void this.load();
	}

	#forget() {
		if (this.#user === undefined && this.document.status === 'idle') return;
		this.#user = undefined;
		this.document.clear();
	}
}
